import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DocumentNode } from 'graphql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';

import cvlApplicationStatusToReadable from 'utils/enums/cvl-application-status-to-readable';
import cvlDocumentTypeToReadable from 'utils/enums/cvl-document-type-to-readable';
import cvlPaymentStatusToReadable from 'utils/enums/cvl-payment-status-to-readable';
import cvlQualificationCriteriaToReadable from 'utils/enums/cvl-qualification-criteria-to-readable';

const INVESTMENT_ENTITY_ACCREDITATION_APPLICATION_QUERY = gql(`
  query getInvestmentEntityAccreditationApplication($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      status
      paymentStatus
      validityPeriodOfAccreditation
      typeOfInvestor
      investorCategory
      name
      firstName
      middleName
      lastName
      dob
      pan
      mobile
      email
      addressLineOne
      addressLineTwo
      addressLineThree
      city
      district
      state
      country
      pincode
      createdAt
      updatedAt
      financialDetails {
        qualificationCriteria
        annualIncomeYear1
        annualIncomeYear2
        netWorthYear1
        netWorthYear2
        financialAssetsYear1
        financialAssetsYear2
      }
      documents {
        id
        documentType
        documentName
        documentUrl
        createdAt
      }
    }
  }
`) as DocumentNode;

const TYPE_OF_INVESTOR_LABELS: Record<string, string> = {
  '1': 'Indian Resident',
  '2': 'Foreign Resident',
};

const INVESTOR_CATEGORY_LABELS: Record<string, string> = {
  '1': 'Individual',
};

const VALIDITY_PERIOD_LABELS: Record<string, string> = {
  '1': '2 years',
  '2': '3 years',
};

const InvestmentEntityAccreditationApplication: FC = () => {
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(
    INVESTMENT_ENTITY_ACCREDITATION_APPLICATION_QUERY,
    {
      variables: {
        investmentEntityId,
      },
    }
  );

  if (loading) return <LoadingIndicator />;

  const application = data?.investorAccreditationApplication;

  if (!application) {
    return (
      <div className="mt-4 rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        This investor has not started a CVL accreditation application.
      </div>
    );
  }

  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  const fullName =
    [application.firstName, application.middleName, application.lastName]
      .filter(Boolean)
      .join(' ') || application.name;

  const address =
    [
      application.addressLineOne,
      application.addressLineTwo,
      application.addressLineThree,
      application.city,
      application.district,
      application.state,
      application.country,
      application.pincode,
    ]
      .filter(Boolean)
      .join(', ') || undefined;

  const { label: statusLabel, color: statusColor } = cvlApplicationStatusToReadable(
    application.status
  );
  const { label: paymentStatusLabel, color: paymentStatusColor } = cvlPaymentStatusToReadable(
    application.paymentStatus
  );

  return (
    <div className="flex flex-col gap-4 mt-4">
      <DetailsPanel
        title="Registration"
        data={[
          {
            label: 'Application Status',
            value: statusLabel,
            type: 'CUSTOM',
            customValue: () => <Badge label={statusLabel} color={statusColor} showIndicator />,
          },
          {
            label: 'Payment Status',
            value: paymentStatusLabel,
            type: 'CUSTOM',
            customValue: () => (
              <Badge label={paymentStatusLabel} color={paymentStatusColor} showIndicator />
            ),
          },
          { label: 'Name', value: fullName },
          { label: 'Date of Birth', value: application.dob },
          { label: 'PAN', value: application.pan },
          { label: 'Mobile', value: application.mobile },
          { label: 'Email', value: application.email },
          {
            label: 'Type of Investor',
            value:
              TYPE_OF_INVESTOR_LABELS[application.typeOfInvestor] || application.typeOfInvestor,
          },
          {
            label: 'Investor Category',
            value:
              INVESTOR_CATEGORY_LABELS[application.investorCategory] ||
              application.investorCategory,
          },
          {
            label: 'Validity Period',
            value:
              VALIDITY_PERIOD_LABELS[application.validityPeriodOfAccreditation] ||
              application.validityPeriodOfAccreditation,
          },
          { label: 'Applied On', value: application.createdAt, type: 'DATETIME' },
          { label: 'Last Updated', value: application.updatedAt, type: 'DATETIME' },
        ]}
      />

      {address && <DetailsPanel title="Address" data={[{ label: 'Address', value: address }]} />}

      {application.financialDetails && (
        <DetailsPanel
          title="Financial Details"
          data={[
            {
              label: 'Qualification Criteria',
              value: cvlQualificationCriteriaToReadable(
                application.financialDetails.qualificationCriteria
              ).label,
              type: 'CUSTOM',
              customValue: () => {
                const { label, color } = cvlQualificationCriteriaToReadable(
                  application.financialDetails!.qualificationCriteria
                );
                return <Badge label={label} color={color} />;
              },
            },
            {
              label: 'Annual Income (Latest FY)',
              value: application.financialDetails.annualIncomeYear1,
              type: 'CURRENCY',
            },
            {
              label: 'Net Worth (Latest FY)',
              value: application.financialDetails.netWorthYear1,
              type: 'CURRENCY',
            },
            {
              label: 'Financial Assets (Latest FY)',
              value: application.financialDetails.financialAssetsYear1,
              type: 'CURRENCY',
            },
            {
              label: 'Annual Income (Prior FY)',
              value: application.financialDetails.annualIncomeYear2,
              type: 'CURRENCY',
            },
            {
              label: 'Net Worth (Prior FY)',
              value: application.financialDetails.netWorthYear2,
              type: 'CURRENCY',
            },
            {
              label: 'Financial Assets (Prior FY)',
              value: application.financialDetails.financialAssetsYear2,
              type: 'CURRENCY',
            },
          ]}
        />
      )}

      <div className="flex flex-col gap-2">
        <p className="font-medium">Documents</p>
        <DataTable
          data={application.documents?.filter(Boolean) || []}
          columns={[
            {
              label: 'Type',
              fieldName: 'documentType',
              type: 'CUSTOM',
              customValue: value => cvlDocumentTypeToReadable(value),
            },
            {
              label: 'Name',
              fieldName: 'documentName',
            },
            {
              label: 'Uploaded On',
              fieldName: 'createdAt',
              type: 'DATETIME',
            },
            {
              label: 'Download',
              fieldName: 'documentUrl',
              type: 'CUSTOM',
              customValue: value => (
                <a
                  className="text-blue-500 hover:underline"
                  href={value}
                  target="_blank"
                  rel="noreferrer"
                >
                  View
                </a>
              ),
            },
          ]}
          emptyListTitle="No documents uploaded yet"
        />
      </div>
    </div>
  );
};

export default InvestmentEntityAccreditationApplication;
